export type WorkSearchEvent = {
  id?: string | number;
  title?: string;
  start: Date;
  end: Date;
  allDay?: boolean;
  resource?: {
    installationId?: unknown;
    phaseTitle?: string | null;
    clientName?: string | null;
    technicianName?: string | null;
    inProgress?: boolean;
    status?: string;
  };
};

export type WorkSearchMetadata = {
  id: number;
  name?: string | null;
  installationCode?: string | null;
  canonicalCustomerName?: string | null;
  clientName?: string | null;
};

export type WorkSearchResult = {
  id: number;
  label: string;
  customer: string;
  periods: Array<{ key: string; start: Date; end: Date | null; label: string }>;
  searchText: string;
};

const clean = (value: unknown) => typeof value === "string" && !/^(null|undefined|nan)$/i.test(value.trim()) ? value.trim() : "";
const normalize = (value: string) => value.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLocaleLowerCase("es");

export function parseWorkId(value: string | null): number | null {
  if (!value || !/^\d+$/.test(value)) return null;
  const id = Number(value);
  return Number.isSafeInteger(id) && id > 0 ? id : null;
}

// Receives only the active view's already-authorized events, across all months.
// No inference from updatedAt, planned dates in Real, or other-view fallback.
export function indexCalendarWorks(events: WorkSearchEvent[], metadata: WorkSearchMetadata[]): WorkSearchResult[] {
  const byId = new Map<number, WorkSearchResult>();
  for (const inst of metadata) {
    const name = clean(inst.name);
    const code = clean(inst.installationCode);
    const customer = clean(inst.canonicalCustomerName) || clean(inst.clientName);
    byId.set(inst.id, { id: inst.id, label: [code, name || customer || "Obra"].filter(Boolean).join(" · "),
      customer, searchText: normalize([code, name, customer].join(" ")), periods: [] });
  }
  for (const event of events) {
    const id = Number(event.resource?.installationId);
    if (!Number.isSafeInteger(id) || id <= 0 || !(event.start instanceof Date) || !(event.end instanceof Date)
      || !Number.isFinite(event.start.getTime()) || !Number.isFinite(event.end.getTime()) || event.end < event.start) continue;
    const customer = clean(event.resource?.clientName);
    const work = byId.get(id) ?? { id, label: customer || "Obra del calendario", customer,
      searchText: normalize(customer), periods: [] };
    // RBC all-day end is exclusive; its presentation must not add a phantom day.
    const end = new Date(event.end);
    if (event.allDay && end > event.start) end.setDate(end.getDate() - 1);
    work.periods.push({ key: String(event.id ?? `${id}-${event.start.toISOString()}`), start: event.start,
      end: event.resource?.inProgress ? null : end,
      label: clean(event.resource?.phaseTitle) || clean(event.resource?.technicianName) || clean(event.title) || "Periodo registrado" });
    byId.set(id, work);
  }
  return Array.from(byId.values()).map(work => ({ ...work,
    periods: work.periods.sort((a, b) => a.start.getTime() - b.start.getTime() || a.key.localeCompare(b.key)),
  })).sort((a, b) => a.label.localeCompare(b.label, "es"));
}

export function searchCalendarWorks(works: WorkSearchResult[], query: string, selectedId: number | null): WorkSearchResult[] {
  const terms = normalize(query.trim()).split(/\s+/).filter(Boolean);
  if (!terms.length) return selectedId ? works.filter(work => work.id === selectedId) : [];
  return works.filter(work => terms.every(term => work.searchText.includes(term)));
}
